/**
 * Blog search scoped to the current blog's articles only.
 */
class BlogSearch extends HTMLElement {
  connectedCallback() {
    this._form = this.querySelector('form');
    this._input = this._form.elements.q;
    this._suggestions = this.querySelector('.blog-banner__search-suggestions');
    this._blogUrl = this.dataset.blogUrl;
    this._articles = this._parseArticles();
    this._debounceTimer = null;

    this._syncInputFromUrl();
    this._form.addEventListener('submit', this._onFormSubmitted.bind(this));
    this._input.addEventListener('input', () => {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this._renderSuggestions(), 280);
    });
    this._input.addEventListener('focus', () => {
      if (this._input.value.trim()) {
        this._renderSuggestions();
      }
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this._hideSuggestions();
      }
    });
  }

  _parseArticles() {
    const script = this.querySelector('[data-blog-search-articles]');

    if (script) {
      try {
        return JSON.parse(script.textContent);
      } catch {
        return [];
      }
    }

    try {
      return JSON.parse(this.dataset.articles || '[]');
    } catch {
      return [];
    }
  }

  _syncInputFromUrl() {
    const urlQuery = new URLSearchParams(window.location.search).get('q');
    if (urlQuery) {
      this._input.value = urlQuery;
    }
  }

  _onFormSubmitted(event) {
    if (!this._input.value.trim()) {
      event.preventDefault();
    }
  }

  _hideSuggestions() {
    this._suggestions.classList.remove('is-visible');
    window.setTimeout(() => {
      if (!this._suggestions.classList.contains('is-visible')) {
        this._suggestions.hidden = true;
      }
    }, 250);
    this._suggestions.innerHTML = '';
    this._input.setAttribute('aria-expanded', 'false');
  }

  _buildResultUrl(query) {
    const separator = this._blogUrl.includes('?') ? '&' : '?';
    return `${this._blogUrl}${separator}q=${encodeURIComponent(query)}`;
  }

  _renderSuggestions() {
    const query = this._input.value.trim();
    if (!query) {
      return this._hideSuggestions();
    }

    const queryLower = query.toLowerCase();
    const seen = new Set();
    const suggestions = [];

    if (!seen.has(queryLower)) {
      seen.add(queryLower);
      suggestions.push({ text: query, url: this._buildResultUrl(query) });
    }

    this._articles.forEach((article) => {
      const title = (article.title || '').trim();
      const titleLower = title.toLowerCase();
      if (!title || !titleLower.includes(queryLower) || seen.has(titleLower)) return;
      seen.add(titleLower);
      suggestions.push({ text: title, url: this._buildResultUrl(title) });
    });

    if (!suggestions.length) {
      return this._hideSuggestions();
    }

    const list = document.createElement('ul');
    list.className = 'blog-banner__search-suggestions-list';

    suggestions.slice(0, 8).forEach((suggestion) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'option');
      const link = document.createElement('a');
      link.href = suggestion.url;
      link.textContent = suggestion.text;
      item.appendChild(link);
      list.appendChild(item);
    });

    this._suggestions.innerHTML = '';
    this._suggestions.appendChild(list);
    this._suggestions.hidden = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._suggestions.classList.add('is-visible');
      });
    });
    this._input.setAttribute('aria-expanded', 'true');
  }
}

if (!window.customElements.get('blog-search')) {
  window.customElements.define('blog-search', BlogSearch);
}
